import React, {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from './store';
import {darkTheme, lightTheme} from './configuration/mui/theme';
import {ThemeProvider} from '@mui/material/styles';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import Pages from './components/Pages';
import {CssBaseline} from '@mui/material';
import Lottie from 'lottie-react';
import loadingAnimation from './assets/lottie/loading.json';
import {Flip, ToastContainer} from 'react-toastify';
import api from './api/axios';
import {setAuthState, setMfaRequired} from './store/slices/authSlice';
import {getAuthStateFromLocalStorage} from './components/utils/LocalStorageAuth';
import {fetchCurrencyRate} from './store/slices/currencySlice';
import DepositWithdrawModal from './components/navbar/wallet/DepositWithdrawModal';
import {DepositWithdrawModalProvider} from './components/navbar/wallet/DepositWithdrawModalContext';
import SignupFlowModal from './components/user/sign-in/SignupFlowModal';
import {useLocation} from 'react-router-dom';
import ReactGA from 'react-ga4';
import {setExtensionInstalled} from './store/slices/appSlice';
import CrispChat from './components/live-chat/CrispChat';
import {setupBrowserNotifications} from './components/utils/NotificationHandler';
import {getMessaging, onMessage} from "firebase/messaging";
import {setAdminState} from "./store/slices/adminSlice";

function App() {
    const dispatch = useDispatch<AppDispatch>();
    const reactLocation = useLocation();

    const isDarkMode = useSelector((state: RootState) => state.darkMode.isDarkMode);
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const isMfaRequired = useSelector((state: RootState) => state.auth.isMfaRequired);
    const currency = useSelector((state: RootState) => state.currency.currency);
    const isExtensionInstalled = useSelector((state: RootState) => state.app.isExtensionInstalled);

    const auth = useSelector((state: RootState) => state.auth);

    // Map auth state to Crisp user format
    const crispUser = isAuthenticated ? {
        nickname: auth.selectedSteamAccount?.profileName ?? undefined,
        email: auth.userMail ?? undefined,
        avatar: auth.selectedSteamAccount?.avatarUrl ?? undefined,
    } : null;

    // Track extension installation state
    useEffect(() => {
        const handleExtensionEvent = () => {
            dispatch(setExtensionInstalled(true));
        };

        // Listen for the event
        globalThis.addEventListener('MOONSKINS_EXTENSION_INSTALLED', handleExtensionEvent);

        // Immediate check: If the extension script already finished before React mounted
        if ((globalThis as any).MOONSKINS_EXTENSION_ENABLED === true) {
            dispatch(setExtensionInstalled(true));
        }

        return () => globalThis.removeEventListener('MOONSKINS_EXTENSION_INSTALLED', handleExtensionEvent);
    }, [dispatch]);

    // Sync and Heartbeat interval
    useEffect(() => {
        if (!isAuthenticated) return;

        const triggerSyncAndHeartbeat = async () => {
            try {
                const res = await api.get('/sync');
                dispatch(setAuthState(getAuthStateFromLocalStorage(res.data)));
                const adminRes = await api.get('/admin-sync');
                dispatch(setAdminState(adminRes.data));
            } catch (err) {
            }

            // Heartbeat interval if extension is missing
            if (!isExtensionInstalled) {
                try {
                    await api.get('/heartbeat');
                } catch (err) {
                }
            }
        };

        const interval = setInterval(triggerSyncAndHeartbeat, 30 * 1000);

        return () => clearInterval(interval);
    }, [isAuthenticated, isExtensionInstalled, dispatch]);

    const checkAuth = useCallback(async () => {
        try {
            const res = await api.get('/check-authentication');

            if (res.data.mfaRequired) {
                dispatch(setMfaRequired(true));
            } else if (res.data.authenticated) {
                // Basic auth set, now fetch full SyncDTO data
                dispatch(setAuthState({
                    isAuthenticated: true,
                    isMfaRequired: false,
                    userId: res.data.userId,
                    selectedSteamAccount: null,
                    steamAccounts: [],
                    userMail: null,
                    role: null,
                    kycStatus: null,
                    signUpStep: 'NONE',
                }));
                dispatch(setMfaRequired(false));

                try {
                    const syncRes = await api.get('/sync');
                    dispatch(setAuthState(getAuthStateFromLocalStorage(syncRes.data)));
                    const adminRes = await api.get('/admin-sync');
                    dispatch(setAdminState(adminRes.data));
                } catch (syncErr) {
                    console.error('Failed to fetch initial sync data:', syncErr);
                }
            } else {
                dispatch(
                    setAuthState({
                        isAuthenticated: false,
                        isMfaRequired: false,
                        selectedSteamAccount: null,
                        steamAccounts: [],
                        userId: null,
                        userMail: null,
                        role: null,
                        kycStatus: null,
                        signUpStep: 'NONE',
                    })
                );
                dispatch(setMfaRequired(false));
            }
        } catch (err) {
            dispatch(
                setAuthState({
                    isAuthenticated: false,
                    isMfaRequired: false,
                    selectedSteamAccount: null,
                    steamAccounts: [],
                    userId: null,
                    userMail: null,
                    role: null,
                    kycStatus: null,
                    signUpStep: 'NONE',
                })
            );
        }
    }, [dispatch]);


    useEffect(() => {
        if (!isAuthenticated) return;

        /** Safe execution wrapper to avoid unhandled rejections */
        const initNotifications = async () => {
            try {
                await setupBrowserNotifications();
            } catch (error) {
                console.warn('Failed to sync notification token:', error);
            }
        };

        initNotifications();

        window.addEventListener('focus', initNotifications);

        return () => {
            window.removeEventListener('focus', initNotifications);
        };
    }, [isAuthenticated]);

    // Shows Notification even if the site is in the foreground
    useEffect(() => {
        const messaging = getMessaging();

        // Foreground handler
        const unsubscribe = onMessage(messaging, (payload) => {
            console.log('Message received in foreground: ', payload);

            if (payload.notification) {
                const title = payload.notification.title || 'Moonskins';
                const body = payload.notification.body || '';
                const image = payload.notification.image || '';

                if (Notification.permission === 'granted') {
                    new Notification(title, {
                        body: body,
                        icon: image,
                        data: {
                            url: payload.data?.url
                        }
                    });
                }
            }
        });

        return () => unsubscribe();
    }, []);


    // Theme management
    useEffect(() => {
        const root = document.documentElement;
        let themeColorMeta = document.querySelector('meta[name="theme-color"]');

        if (!themeColorMeta) {
            themeColorMeta = document.createElement('meta');
            themeColorMeta.setAttribute('name', 'theme-color');
            document.head.appendChild(themeColorMeta);
        }

        if (isDarkMode) {
            root.classList.add('dark');
            themeColorMeta.setAttribute('content', '#262626');
        } else {
            root.classList.remove('dark');
            themeColorMeta.setAttribute('content', '#ffffff');
        }
    }, [isDarkMode]);

    // Currency sync
    useEffect(() => {
        dispatch(fetchCurrencyRate(currency));
    }, [currency, dispatch]);

    // Initial auth check
    useEffect(() => {
        if (isAuthenticated === null) {
            checkAuth().catch(console.error);
        }
    }, [checkAuth, isAuthenticated]);

    // Analytics
    useEffect(() => {
        // @ts-ignore
        if (ReactGA.isInitialized) {
            ReactGA.send({hitType: 'pageview', page: reactLocation.pathname + reactLocation.search});
        }
    }, [reactLocation]);


    if (isAuthenticated === null && !isMfaRequired) {
        return (
            <div className="flex items-center justify-center h-screen bg-white dark:bg-black">
                <Lottie animationData={loadingAnimation} loop={true} className="h-96 md:h-[400px]"/>
            </div>
        );
    }

    return (
        <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
            <CssBaseline/>
            <ToastContainer
                position="bottom-left"
                transition={Flip}
                autoClose={4000}
                theme={isDarkMode ? 'dark' : 'light'}
            />
            <DepositWithdrawModalProvider>
                <CrispChat user={crispUser}/>
                <Pages/>
                <SignupFlowModal/>
                <DepositWithdrawModal/>
            </DepositWithdrawModalProvider>
        </ThemeProvider>
    );
}

export default App;